import { defineStore } from "pinia"
import { type ApiResponse } from "~/types/api"

export const META_KEYS = {
	// rate limit
	META_REQUEST_PER_HOUR: "request-per-hour",
	META_ADMIN_LOGIN: "admin-login",
	META_USER_LOGIN: "user-login",
	META_USER_REGISTER: "user-register",
	META_USER_CHANGE_PASSWORD: "user-change-password",
	// user block
	META_BLOCK_USER_MAX_FAILED_ATTEMPT: "block-user-max-failed-attempt",
	META_BLOCK_USER_DURATION: "block-user-duration",
	// info
	META_SOCIAL_INSTAGRAM: "social-instagram",
	META_SOCIAL_WHATSAPP: "social-whatsapp",
	META_CONTACT_EMAIL: "contact-email",
	META_CONTACT_ADDRESS: "contact-address",
	META_TERMS_OF_USE: "terms-of-use",
	META_FINANCIAL_RULES: "financial-rules",
	META_PRIVACY: "privacy",
	META_CONTACT_US: "contact-us",
	META_ABOUT_US: "about-us",
	META_PAYROLL_COLUMNS: "payroll-columns",
	META_PAYROLL_REWARD_COLUMNS: "payroll-reward-columns",
	// notification
	META_PAYROLL_NOTIFICATION_MESSAGE: "payroll-notification-message",
	META_PERFORMANCE_REVIEW_NOTIFICATION_MESSAGE: "performance-review-notification-message",
	META_COMPANY_USER_REGISTRATION_NOTIFICATION_MESSAGE: "company-user-registration-notification-message"
} as const

export type MetaKeysType = (typeof META_KEYS)[keyof typeof META_KEYS]

// meta :: get meta
export interface GetMetaRequest {
	name: MetaKeysType
}
export interface GetMetaResponse extends ApiResponse {
	data: any
}
const GetMeta = {
	method: "GET" as const,
	url: () => `metas/get`
}

// meta :: set meta
export interface SetMetaRequest {
	name: MetaKeysType
	value: any
}
export interface SetMetaResponse extends ApiResponse {}
const SetMeta = {
	method: "POST" as const,
	url: () => `metas/set`
}

// meta :: get all metas
export interface GetAllMetasRequest {}
export interface GetAllMetasResponse extends ApiResponse {
	data: {
		[key in MetaKeysType]?: any
	}
}
const GetAllMetas = {
	method: "GET" as const,
	url: () => `metas/all`
}

// meta :: set all metas
export interface SetAllMetasRequest {
	metas: {
		[key in MetaKeysType]?: any
	}
}
export interface SetAllMetasResponse extends ApiResponse {}
const SetAllMetas = {
	method: "POST" as const,
	url: () => `metas/all`
}

export const useMetaApiUrl = () => ({
	GetMeta,
	SetMeta,
	GetAllMetas,
	SetAllMetas
})

export const useMetaStore = defineStore("meta", () => {
	const state = {
		metas: ref<any>(null),
		isViraEnable: ref(false)
	}

	const getMeta = async (formData: GetMetaRequest) => {
		const r = await $fetch<GetMetaResponse>(GetMeta.url(), {
			...useFetchConfig(),
			method: GetMeta.method,
			query: formData
		})
		return r.data
	}

	const setMeta = async (formData: SetMetaRequest) => {
		const r = await $fetch<SetMetaResponse>(SetMeta.url(), {
			...useFetchConfig(),
			method: SetMeta.method,
			body: formData
		})
		return r.data
	}

	const getAllMetas = async (formData?: GetAllMetasRequest) => {
		const r = await $fetch<GetAllMetasResponse>(GetAllMetas.url(), {
			...useFetchConfig(),
			method: GetAllMetas.method,
			query: formData
		})
		return r.data
	}

	const setAllMetas = async (formData: SetAllMetasRequest) => {
		const r = await $fetch<SetAllMetasResponse>(SetAllMetas.url(), {
			...useFetchConfig(),
			method: SetAllMetas.method,
			body: formData
		})
		return r.data
	}

	return {
		...state,
		getMeta,
		setMeta,
		getAllMetas,
		setAllMetas
	}
})
